// Symphony package-picker runtime shape (returned by GET /api/plans).
//
// Kept separate from the Orion-legacy Stripe catalog (lib/stripe/catalog.ts):
// the Symphony picker sources its plan details from the AgentPlanService
// catalog at runtime, so the client component and the server mapping
// (lib/plans-server.ts) only need to agree on this shape — not on any
// hardcoded prices.
//
// **Every editorial string here is an i18n KEY, not a sentence.** The endpoint runs
// server-side with no locale — the request carries the buyer's language nowhere the
// mapping can see it — so a card name, tagline or feature line built here would ship
// English to all 37 locales. The server therefore emits `pricing.plan.*` catalog keys
// and the picker resolves them with `t()` at render, in the locale the app already
// resolved (see lib/i18n.ts). Numbers (credits) travel as numbers and get formatted,
// not interpolated into a server-built string.

export type Cadence = 'monthly' | 'yearly';

/** A purchasable credit tier within a plan card (Max carries two: Max / Max+). */
export interface PickerOption {
  /** AgentPlan id — sent to checkout, which resolves the Stripe price server-side. */
  agentPlanId: string;
  /**
   * Monthly credit allowance. The credits line / dropdown label is built from THIS number
   * at render (`pricing.monthlyCredits`), so the digits group the way the reader's locale
   * groups them instead of the server's.
   */
  credits: number;
  /** Small note under the credits line, e.g. "Credits 12% cheaper". */
  note?: string;
  /**
   * Headline monthly price in the plan's currency (major units), or `undefined` when this cadence
   * has no resolved Stripe price. `undefined` (never `0`) so the picker can distinguish "unpriced"
   * from a genuine 0 and refuse to display/charge a cadence we couldn't price.
   */
  monthlyAmount?: number;
  /** Full yearly price in the plan's currency (per-month headline derived as /12), or `undefined` — see monthlyAmount. */
  yearlyAmount?: number;
  /** ISO currency code of the resolved Stripe price (e.g. "usd", "gbp"). Drives the display symbol. */
  currency?: string;
  /** Catalog keys for this option's "What you get" lines (e.g. `pricing.plan.max.feature1`). */
  featureKeys: string[];
  /** One-time top-up pack THIS option's current subscriber may buy (Max+ only today). */
  topup?: PickerTopup;
}

/**
 * A one-time top-up credit pack offered on a specific plan option. Rendered ONLY when the buyer's
 * CURRENT plan is the option carrying it (a top-up adds credits to the subscription you're on) —
 * other options, other cards, and non-subscribers see nothing.
 */
export interface PickerTopup {
  /** AgentPack id — sent to checkout (period ONE_TIME), which resolves the Stripe price server-side. */
  packId: string;
  /** Credits granted per purchase (raw number — the button localizes its own label around it). */
  credits: number;
  /**
   * One-time price in the plan's currency (major units). Always present — a pack whose price
   * didn't resolve is never surfaced (the server omits `topup` entirely), so the button can
   * never render without a real amount.
   */
  amount: number;
  /** ISO currency code of the resolved Stripe price (e.g. "usd", "gbp"). */
  currency?: string;
}

/**
 * One card on the picker. **Every card is purchasable** — the free tier is not offered here (see
 * `lib/plans-server`), so there is no card without options, no card-level feature copy, and no
 * card whose CTA does something other than start a purchase or a plan change.
 */
export interface PickerPlan {
  /** Card key: basic | pro | max. */
  key: string;
  /** Catalog key for the card's tier name (e.g. `pricing.plan.max.name`). */
  nameKey: string;
  /** Catalog key for the one-line tagline under the name. */
  taglineKey: string;
  recommended?: boolean;
  /** Purchasable options, at least one. Each option may carry its own top-up pack. */
  options: PickerOption[];
}

/**
 * The cards from a `/api/plans` payload that this picker can actually render: the ones carrying at
 * least one option. Applied where the response is parsed, so `PickerPlan.options`'s "at least one"
 * is true of every card downstream rather than merely intended.
 *
 * It exists for one window. The page bundle and the API route ship in the same deployable, but a
 * ROLLING deploy runs both versions at once — so a browser that loaded the new bundle can put its
 * `/api/plans` call on a pod that has not updated yet and still emits the Free card. The old card
 * carries no options, and a card with no options renders as a real card that cannot be bought:
 * "Not available monthly" where the price goes, a disabled CTA, and an empty feature list. On a
 * paywall that reads as a broken product rather than as a plan that is gone.
 *
 * Deliberately keyed on `options`, not on any leftover `free` marker: the same shape would arrive
 * from a plan whose prices all failed to resolve, and the fix is the same either way — do not draw
 * a card nobody can buy.
 */
export function purchasablePlans(plans: PickerPlan[]): PickerPlan[] {
  return plans.filter((plan) => plan.options.length > 0);
}

/**
 * Which cadences a resolved catalog actually prices, or `null` before it has loaded.
 *
 * **A market can price only ONE cadence.** India is the first: RBI requires additional authentication
 * on every recurring card debit over ₹15,000, which no annual price of ours clears, so India opens with
 * monthly prices and no yearly ones. Nothing else in the picker assumed a cadence could be entirely
 * absent — the component initialises `cadence` to `'yearly'` — so such a market opened on Yearly and
 * read "not available yearly" on every card before the buyer touched anything. The catalog was right
 * and the first paint was still wrong.
 *
 * Lives here rather than in the component for the same reason `topupPackOwnerPlanId` does: it is a
 * pure decision over the catalog, and there is no render test for `SymphonyPricing` to pin it with.
 * `undefined` (never `0`) marks an unpriced cadence — see `PickerOption.monthlyAmount` — so this must
 * test for null-ness and not truthiness, or a genuinely free-priced option would read as unpriced.
 */
export function pricedCadences(plans: PickerPlan[] | null): PricedCadences | null {
  if (!plans) return null;
  const options = plans.flatMap((p) => p.options);
  return {
    monthly: options.some((o) => o.monthlyAmount != null),
    yearly: options.some((o) => o.yearlyAmount != null),
  };
}

/** Which cadences a market prices. `null` at the call sites means "catalog not loaded yet". */
export interface PricedCadences {
  monthly: boolean;
  yearly: boolean;
}

/**
 * The cadence to RENDER, from what the buyer selected and what the market actually prices.
 *
 * **This must be derived during render, never corrected in an effect**, and that distinction is the
 * entire point of the function. `useEffect` runs after commit *and* after the browser paints, so a
 * monthly-only catalog would paint one full frame of "not available yearly" on every card — with the
 * toggle already hidden, because that IS computed during render — and only then flip to monthly. That
 * is precisely the first-paint bug the monthly-only handling exists to prevent, moved one frame later
 * where it is harder to notice and impossible to unit-test. It is reachable whenever `/api/premium`
 * resolves before `/api/plans`.
 *
 * A `null` `priced` (catalog not loaded) returns the selection untouched: there is nothing to correct
 * toward yet, and the cards have no prices to show either way.
 *
 * The buyer's own choice is never overridden when yearly IS priced — the toggle only renders in that
 * case, so a selection can only exist where it is honourable.
 */
export function effectiveCadence(selected: Cadence, priced: PricedCadences | null): Cadence {
  // Guarded on `monthly` too: with NEITHER cadence priced (an allowed-but-unpriced market — see
  // picker-geo) there is nothing to fall back to, and switching would swap one empty view for
  // another while hiding the toggle that shows something is wrong.
  if (priced && !priced.yearly && priced.monthly) return 'monthly';
  return selected;
}

/** One of the user's subscriptions (from GET /api/premium). Dates arrive as ISO strings over JSON. */
export interface PickerSubscription {
  planId: string;
  status?: string;
  period?: string;
  providerSubscriptionId?: string;
  currentPeriodEnd?: string;
}

/** The signed-in user's premium state (returned by GET /api/premium). */
export interface PickerPremium {
  creditsBalance: number;
  subscriptionCredits: number;
  topupCredits: number;
  subscriptions: PickerSubscription[];
}

/**
 * The subscription fields the paid-active rule reads. Deliberately structural, so the ONE rule below
 * serves both the picker's `PickerSubscription` (JSON strings over the wire) and the server's
 * `PremiumSubscription` (enums + Dates) — the checkout enforcement in `lib/topup-eligibility.ts` must
 * decide "is this a real paid subscription" exactly as the UI does, or the button and the endpoint
 * disagree about the same account.
 */
export interface PaidActiveSubscriptionFields {
  planId: string;
  status?: string;
  providerSubscriptionId?: string;
}

/**
 * A subscription counts as PAID only when it carries a Stripe `providerSubscriptionId` and is active.
 * A FREE-tier subscription is created ACTIVE with NO `providerSubscriptionId`, so it must NOT read as
 * "subscribed": otherwise the picker routes its paid cards through SwitchPlan (which has no Stripe
 * subscription to modify) — breaking the free→paid upgrade — and Settings shows a spurious
 * "Active plan / Cancel". CANCELED/expired states are excluded too.
 */
export function isPaidActiveSubscription(sub: PaidActiveSubscriptionFields): boolean {
  return Boolean(
    sub.planId && sub.providerSubscriptionId && (sub.status === 'ACTIVE' || sub.status === 'PAST_DUE'),
  );
}

/**
 * The AgentPlan ids the user is treated as subscribed to: their real paid (Stripe-backed)
 * subscriptions, plus the just-checked-out plan during the optimistic post-purchase window (before the
 * async webhook grant lands — that seed has no `providerSubscriptionId` yet but is paid by
 * construction). A free-tier subscription is excluded — see {@link isPaidActiveSubscription}.
 */
export function paidActivePlanIds(
  premium: PickerPremium | null,
  optimisticAgentPlanId?: string | null,
): Set<string> {
  const ids = new Set<string>();
  for (const s of premium?.subscriptions ?? []) {
    if (isPaidActiveSubscription(s)) ids.add(s.planId);
  }
  if (optimisticAgentPlanId) ids.add(optimisticAgentPlanId);
  return ids;
}

/**
 * Does the buyer's own plan sit ABOVE the tier the catalog recommends?
 *
 * "Recommended" is an upsell, so a Max/Max+ subscriber was being pointed at Pro — a nudge one tier
 * DOWN from what they already pay for. The whole recommended treatment (the blue plate, the blue
 * stroke, the lime CTA) drops once the buyer outranks it.
 *
 * Ranked by CREDITS, not by card position: the left→right order is a Figma layout choice a redesign
 * can reorder, while the monthly allotment is the commercial size of a tier and moves with it.
 * Compared against the recommended card's BIGGEST option, and strictly greater — a subscriber to the
 * recommended tier itself is not above it, and their own card is already drawn as the current plan.
 *
 * Cadence-agnostic on purpose: a Max subscriber must not be pushed toward Pro in EITHER cadence.
 * `activePlanIds` carries the optimistic post-purchase seed, so this takes effect on the buyer's own
 * click rather than waiting for the webhook grant.
 */
export function outranksRecommendedTier(
  plans: PickerPlan[] | null,
  activePlanIds: ReadonlySet<string>,
): boolean {
  if (!plans || activePlanIds.size === 0) return false;
  const recommendedCredits = plans
    .filter((p) => p.recommended === true)
    .flatMap((p) => p.options.map((o) => o.credits));
  if (recommendedCredits.length === 0) return false;
  const ceiling = Math.max(...recommendedCredits);
  return plans.some((p) => p.options.some((o) => activePlanIds.has(o.agentPlanId) && o.credits > ceiling));
}

/**
 * Resolve a deeplinked top-up request (?topUpPackageId=…, sent by the mobile apps) to the pack the
 * picker would actually offer: the requested pack must be the one carried by a plan OPTION the
 * user holds a REAL (paid, Stripe-backed) subscription to — the same current-plan rule the top-up
 * CTA renders under (Max+ only today). Anything else — unknown pack, a pack belonging to a plan
 * the user isn't on, no paid subscription at all — resolves to null, and the caller silently shows
 * the plain picker instead of a checkout the server would refuse.
 */
export function resolveAutoTopupPack(
  plans: PickerPlan[] | null,
  realPlanIds: ReadonlySet<string>,
  packId: string | null,
): PickerTopup | null {
  if (!packId || !plans) return null;
  for (const plan of plans) {
    const option = plan.options.find((o) => o.topup?.packId === packId);
    if (option && realPlanIds.has(option.agentPlanId)) return option.topup ?? null;
  }
  return null;
}
